import React from 'react';
import { format } from 'date-fns';

import { DetailRow } from '../../../components/DetailRow';
import {
  IBankAccount,
  SurepayAccountStatus,
  SurepayNameMatchResult,
  SurepayOutcome,
} from '../models/Customer';

type SurePayPanelProps = {
  bankAccount?: IBankAccount;
};

/**
 * Note "Not verified" (we asked, SurePay did not answer) is deliberately worded
 * differently from the "Not checked" empty state below (we never asked). They
 * are different facts and an admin has to be able to tell them apart — one means
 * a SurePay or certificate problem to investigate, the other just means the
 * quote predates the feature.
 */
const OUTCOME_LABELS: Record<SurepayOutcome, string> = {
  valid: 'Valid',
  invalid: 'Invalid',
  unverified: 'Not verified',
};

const OUTCOME_BADGES: Record<SurepayOutcome, string> = {
  valid: 'badge-light-success',
  invalid: 'badge-light-danger',
  unverified: 'badge-light-warning',
};

const NAME_MATCH_LABELS: Record<SurepayNameMatchResult, string> = {
  MATCH: 'Name matches',
  CLOSE_MATCH: 'Close match',
  NO_MATCH: 'No match',
  NOT_CHECKED: 'Not checked',
};

const NAME_MATCH_BADGES: Record<SurepayNameMatchResult, string> = {
  MATCH: 'badge-light-success',
  CLOSE_MATCH: 'badge-light-warning',
  NO_MATCH: 'badge-light-danger',
  NOT_CHECKED: 'badge-light',
};

const ACCOUNT_STATUS_LABELS: Record<SurepayAccountStatus, string> = {
  ACTIVE: 'Active',
  NOT_FOUND: 'Not found',
  UNKNOWN: 'Unknown',
};

/** SurePay's own tokens, spelled out. Anything unrecognised is shown verbatim. */
const ACCOUNT_AGE_LABELS: Record<string, string> = {
  GREATER_THAN_ONE_YEAR: 'More than one year',
  LESS_THAN_ONE_YEAR: 'Less than one year',
  LESS_THAN_THREE_MONTHS: 'Less than three months',
};

const ACCOUNT_TYPE_LABELS: Record<string, string> = {
  ORG: 'Business',
  PERSONAL: 'Personal',
};

const PRE_VALIDATION_LABELS: Record<string, string> = {
  PASS: 'Passed',
  FAIL: 'Failed',
};

const Badge: React.FC<{ variant: string; children: React.ReactNode }> = ({
  variant,
  children,
}) => <span className={`badge ${variant} fw-bold fs-8`}>{children}</span>;

/** Anything SurePay may add later still renders, just untranslated. */
const label = (
  map: Record<string, string>,
  value?: string | null
): string | undefined => (value ? map[value] ?? value : undefined);

/** The API returns an ISO-8601 string; show it in the app's usual format. */
const formatCheckedAt = (value: string): string => {
  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime())
    ? value
    : format(parsed, 'yyyy-MM-dd HH:mm');
};

/**
 * What SurePay said about the customer's IBAN when they accepted the quote.
 *
 * Read-only, and deliberately so: the quote flow blocks only on an explicit
 * INVALID account number, so a name mismatch or an account SurePay could not
 * find reaches this screen as a warning that a human has to weigh before the
 * VoipGrid account is provisioned. A sole trader paying from a private account
 * produces a mismatch entirely legitimately, which is why it was never blocked
 * and why this panel exists.
 *
 * The data arrives in the existing view-quote payload — ViewQuoteAction already
 * eager-loads customer.bankAccount and serialises it with a raw toArray(), so
 * the columns appeared here the moment the migration ran. No endpoint, no CRUD
 * function, no re-check button.
 */
export const SurePayPanel: React.FC<SurePayPanelProps> = ({ bankAccount }) => {
  const outcome = bankAccount?.surepay_outcome ?? null;
  const nameMatch = bankAccount?.surepay_name_match_result ?? null;
  const suggestion = bankAccount?.surepay_name_suggestion ?? null;

  // Every quote from before this feature shipped lands here. Saying so plainly
  // is more honest than rendering eight empty rows.
  if (!bankAccount?.surepay_checked_at) {
    return (
      <>
        <div className="separator my-8"></div>
        <h4 className="fw-bolder mb-6">SurePay check</h4>
        <p className="text-muted">
          Not checked. This quote was accepted before the SurePay check existed,
          or the check never ran.
        </p>
      </>
    );
  }

  const needsAttention = nameMatch === 'NO_MATCH' || nameMatch === 'CLOSE_MATCH';

  return (
    <>
      <div className="separator my-8"></div>
      <h4 className="fw-bolder mb-6">SurePay check</h4>

      {needsAttention && (
        <div className="alert alert-warning d-flex align-items-center p-5">
          <div className="d-flex flex-column">
            <span className="fw-bolder">
              The account holder name does not match the account number.
            </span>
            <span>
              Check this before provisioning the account.
              {suggestion &&
                ` According to the bank this account belongs to ${suggestion}.`}
            </span>
          </div>
        </div>
      )}

      {/* An unverified outcome is an operational problem on our side, not
          something wrong with the customer's IBAN. Spelling that out here is
          what stops it being read as "the customer's account is suspect", and
          points at the log that actually explains it. */}
      {outcome === 'unverified' && (
        <div className="alert alert-warning d-flex align-items-center p-5">
          <div className="d-flex flex-column">
            <span className="fw-bolder">
              SurePay could not be reached, so this IBAN was only checked for
              format.
            </span>
            <span>
              The account number is structurally valid but was not confirmed
              with the bank. This points at missing SurePay credentials, an
              expired or unreadable mTLS certificate, or an outage — check
              storage/logs/surepay.log on the server.
            </span>
          </div>
        </div>
      )}

      <DetailRow
        label="Account number"
        value={
          outcome ? (
            <Badge variant={OUTCOME_BADGES[outcome]}>
              {OUTCOME_LABELS[outcome]}
            </Badge>
          ) : undefined
        }
      />

      <DetailRow
        label="Name check"
        value={
          nameMatch ? (
            <Badge variant={NAME_MATCH_BADGES[nameMatch]}>
              {NAME_MATCH_LABELS[nameMatch]}
            </Badge>
          ) : undefined
        }
      />

      <DetailRow label="Name suggestion" value={suggestion ?? undefined} />

      <DetailRow
        label="Account status"
        value={label(ACCOUNT_STATUS_LABELS, bankAccount.surepay_account_status)}
      />

      <DetailRow
        label="Payment pre-validation"
        value={label(
          PRE_VALIDATION_LABELS,
          bankAccount.surepay_payment_pre_validation
        )}
      />

      <DetailRow
        label="Account type"
        value={label(ACCOUNT_TYPE_LABELS, bankAccount.surepay_account_type)}
      />

      <DetailRow
        label="Account age"
        value={label(ACCOUNT_AGE_LABELS, bankAccount.surepay_account_age)}
      />

      <DetailRow
        label="Checked at"
        value={formatCheckedAt(bankAccount.surepay_checked_at)}
      />
    </>
  );
};
